import { useEffect, useRef, useState } from 'react';
import { Banknote, Upload, AlertCircle, Plus, CheckCircle2 } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { accountingApi, ApiError } from '@solara/api-client';
import type { BankAccountDto, BankTransactionDto } from '@solara/api-client';

const STATUS_TONES: Record<string, 'neutral' | 'success' | 'warning' | 'info' | 'danger'> = {
  IMPORTED: 'info',
  SUGGESTED: 'warning',
  REVIEWED: 'warning',
  POSTED: 'success',
  REJECTED: 'danger',
};

const CATEGORIES = ['INCOME', 'EXPENSE', 'BANK_TRANSFER', 'TAX_PAYMENT', 'LOAN', 'INVESTMENT', 'OTHER'];

export default function BankingPage() {
  const { t } = useTranslation('p1');
  const [banks, setBanks] = useState<BankAccountDto[]>([]);
  const [tx, setTx] = useState<BankTransactionDto[]>([]);
  const [activeBank, setActiveBank] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newBank, setNewBank] = useState({ bankName: '', accountNumber: '', iban: '', swift: '', currency: 'AED' });
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [b, t] = await Promise.all([accountingApi.banks(), accountingApi.transactions()]);
      setBanks(b);
      setTx(t);
      if (b[0] && !activeBank) setActiveBank(b[0].id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function addBank() {
    if (!newBank.bankName || !newBank.accountNumber) {
      setError(t('banking.bankRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await accountingApi.createBank(newBank);
      setNewBank({ bankName: '', accountNumber: '', iban: '', swift: '', currency: 'AED' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('banking.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function handleCsv(file: File) {
    if (!activeBank) return;
    setBusy(true);
    setError(null);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        setError(t('banking.csvEmpty'));
        return;
      }
      const result = await accountingApi.importTransactions(activeBank, rows, 'CSV');
      setError(null);
      await load();
      alert(t('banking.imported', { count: result.imported }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setCategory(id: string, category: string) {
    setBusy(true);
    try {
      await accountingApi.categorize(id, { category });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('banking.categorizeFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function postTx(id: string) {
    setBusy(true);
    try {
      await accountingApi.post(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('banking.postFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('banking.title')}</h1>
        <p className="text-fg-muted text-sm">{t('banking.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Banknote className="h-4 w-4" />
            {t('banking.accounts')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {banks.length === 0 ? (
            <p className="py-3 text-sm text-fg-muted">{t('banking.noBanks')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {banks.map((b) => (
                <li key={b.id} className="flex items-center justify-between py-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="active-bank"
                      checked={activeBank === b.id}
                      onChange={() => setActiveBank(b.id)}
                    />
                    <div>
                      <p className="font-medium">{b.bankName} <span className="text-xs text-fg-muted">{b.currency}</span></p>
                      <p className="text-xs text-fg-muted">{b.accountNumberEncrypted}</p>
                    </div>
                  </label>
                  {activeBank === b.id && (
                    <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy}>
                      <Upload className="mr-2 h-3 w-3" />
                      {t('banking.importCsv')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleCsv(f);
              e.target.value = '';
            }}
          />
        </CardBody>
        <CardBody className="border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <Input placeholder={t('banking.bankName')} value={newBank.bankName} onChange={(e) => setNewBank({ ...newBank, bankName: e.target.value })} />
          <Input placeholder={t('banking.accountNumber')} value={newBank.accountNumber} onChange={(e) => setNewBank({ ...newBank, accountNumber: e.target.value })} />
          <Input placeholder={t('banking.iban')} value={newBank.iban} onChange={(e) => setNewBank({ ...newBank, iban: e.target.value })} />
          <Input placeholder={t('banking.swift')} value={newBank.swift} onChange={(e) => setNewBank({ ...newBank, swift: e.target.value })} />
          <Input placeholder={t('banking.currency')} value={newBank.currency} onChange={(e) => setNewBank({ ...newBank, currency: e.target.value })} />
          <Button onClick={addBank} disabled={busy} className="justify-self-start">
            <Plus className="mr-2 h-3 w-3" />
            {t('banking.addBank')}
          </Button>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('banking.transactionsCount', { count: tx.length })}</CardTitle>
        </CardHeader>
        <CardBody>
          {tx.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('banking.noTransactions')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {tx.map((row) => (
                <li key={row.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {new Date(row.transactionDate).toLocaleDateString()} ·{' '}
                        <span className={Number(row.amountMinor) >= 0 ? 'text-success' : 'text-danger'}>
                          {(Number(row.amountMinor) / 100).toLocaleString()} {row.currency}
                        </span>
                      </p>
                      <p className="text-xs text-fg-muted">
                        {row.counterpartyName ?? '—'} · {row.description ?? '—'}
                      </p>
                    </div>
                    <Badge tone={STATUS_TONES[row.status] ?? 'neutral'} className="text-[10px]">
                      {t(`banking.statuses.${row.status}`, { defaultValue: row.status })}
                    </Badge>
                  </div>
                  {row.status !== 'POSTED' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <select
                        className="rounded border border-border bg-surface px-2 py-1 text-xs"
                        value={row.category ?? ''}
                        onChange={(e) => void setCategory(row.id, e.target.value)}
                      >
                        <option value="">{t('banking.categoryPlaceholder')}</option>
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {t(`banking.categories.${c}`, { defaultValue: c })}
                          </option>
                        ))}
                      </select>
                      <Button variant="ghost" onClick={() => void postTx(row.id)} disabled={busy || !row.category}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('banking.post')}
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * Minimal CSV parser for bank statement imports.
 * Accepts the common UAE-bank header layout:
 *   date,value_date,amount,currency,description,counterparty
 * Tolerates BOM, quoted fields, and extra whitespace.
 */
function parseCsv(text: string): Array<{
  transactionDate: string;
  valueDate?: string;
  amountMinor: number;
  currency?: string;
  description?: string;
  counterpartyIban?: string;
  counterpartyName?: string;
}> {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const header = lines[0]!.split(',').map((h) => h.trim().toLowerCase());
  const idx = {
    date: header.findIndex((h) => /^(transaction[_ ]?date|date)$/.test(h)),
    valueDate: header.findIndex((h) => /^(value[_ ]?date)$/.test(h)),
    amount: header.findIndex((h) => /^amount$/.test(h)),
    currency: header.findIndex((h) => /^currency$/.test(h)),
    description: header.findIndex((h) => /^(description|reference|narrative)$/.test(h)),
    counterpartyName: header.findIndex((h) => /^(counterparty|payer|payee|name)$/.test(h)),
    counterpartyIban: header.findIndex((h) => /^iban$/.test(h)),
  };
  if (idx.date === -1 || idx.amount === -1) return [];
  const rows: ReturnType<typeof parseCsv> = [];
  const safe = (cols: string[], at: number): string | undefined => (at >= 0 ? cols[at] : undefined);
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i]!.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const date = safe(cols, idx.date);
    const amountRaw = safe(cols, idx.amount);
    if (!date || !amountRaw) continue;
    const amountFloat = Number(amountRaw.replace(/[, ]/g, ''));
    if (!Number.isFinite(amountFloat)) continue;
    rows.push({
      transactionDate: date,
      valueDate: safe(cols, idx.valueDate),
      amountMinor: Math.round(amountFloat * 100),
      currency: safe(cols, idx.currency),
      description: safe(cols, idx.description),
      counterpartyName: safe(cols, idx.counterpartyName),
      counterpartyIban: safe(cols, idx.counterpartyIban),
    });
  }
  return rows;
}
